/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { OperationVariables, useMutation, useQuery } from '@apollo/client'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import Snackbar from '@material-ui/core/Snackbar'
import MuiAlert from '@material-ui/lab/Alert'
import Icon from '@material-ui/core/Icon'
import { UpdateGeneralSetting } from '@services/app/mutation'
import appClient from '@services'
import { remove } from 'local-storage'
import { useUserDispatcher } from '@hooks'
import { GetPointSettings } from '@services/app/query'
import { IServiceResponse } from '@services/model'
import ConfirmationDialog from './localComponents/ConfirmationDialog'

function Alert(props: any) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    width: 350,
    marginLeft: 8,
    '& > *': {
      margin: theme.spacing(1),
    },
  },
}))

const useImperativeQuery = (query: any, client: any) => {
  const { refetch } = useQuery(query, { client, skip: true })

  const imperativelyCallQuery = (variables: Partial<OperationVariables> | undefined) => {
    return refetch(variables)
  }

  return imperativelyCallQuery
}

interface GeneralSetting {
  id: number
  name: string
  value: string | undefined
}

const TrophyPoint: React.FC = () => {
  const getPointSettings = useImperativeQuery(GetPointSettings, appClient)
  const { clearUserAuthData } = useUserDispatcher()
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const [openSnackBar, setOpenSnackbar] = useState(false)
  const [isGeneralSettingUpdated, setIsGeneralSettingUpdated] = useState(false)
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)

  // Trophy 
  const [medalTrophyId, setMedalTrophyId] = useState(1)
  const [medalTrophy, setMedalTrophy] = useState<string | undefined>('0')
  const [errorMedalTrophy, setErrorMedalTrophy] = useState(false)

  const [cupTrophyId, setCupTrophyId] = useState(1)
  const [cupTrophy, setCupTrophy] = useState<string | undefined>('0')
  const [errorCupTrophy, setErrorCupTrophy] = useState(false)

  const [flameTrophyId, setFlameTrophyId] = useState(1)
  const [flameTrophy, setFlameTrophy] = useState<string | undefined>('0')
  const [errorFlameTrophy, setErrorFlameTrophy] = useState(false)

  const [starTrophyId, setStarTrophyId] = useState(1)
  const [starTrophy, setStarTrophy] = useState<string | undefined>('0')
  const [errorStarTrophy, setErrorStarTrophy] = useState(false)

  const [streakPenaltyId, setStreakPenaltyId] = useState(1)
  const [streakPenalty, setStreakPenalty] = useState<string | undefined>('0')
  const [errorStreakPenalty, setErrorStreakPenalty] = useState(false)

  const [updateGeneralSetting] = useMutation(UpdateGeneralSetting, { client: appClient })

  const goToLogin = () => {
    remove('token')
    clearUserAuthData()
  }

  const handleData = (trophyPointSettings: GeneralSetting[]) => {
    for (const item of trophyPointSettings) {
      switch (item.name) {
        case 'MEDAL_TROPHY_POINT':
          setMedalTrophyId(item.id)
          setMedalTrophy(item.value)
          break
        case 'CUP_TROPHY_POINT':
          setCupTrophyId(item.id)
          setCupTrophy(item.value)
          break
        case 'FLAME_TROPHY_POINT':
          setFlameTrophyId(item.id)
          setFlameTrophy(item.value)
          break
        case 'STAR_TROPHY_POINT':
          setStarTrophyId(item.id)
          setStarTrophy(item.value)
          break
        case 'STREAK_PENALTY':
          setStreakPenaltyId(item.id)
          setStreakPenalty(item.value)
          break
      }
    }
  }

  const retrieveTrophySettings = async () => {
    try {
      const getPointSettingsResp = await getPointSettings(undefined)
      if (getPointSettingsResp != null) {
        const { data: { getAppSettings: { result } } } = getPointSettingsResp
        handleData(result.trophyPointSettings)
      }
    } catch (e) {
      if (e?.graphQLErrors[0]?.extensions?.code == "authorization") {
        goToLogin()
        return
      }
      console.error(e)
    }
  }

  useEffect(() => {
    retrieveTrophySettings()
  }, [])

  const handleSavePointSetting = async () => {
    setOpenConfirmationDialog(false)

    try {
      setIsLoading(true)

      const updateGeneralSettingVariables = {
        settings: [{
          id: medalTrophyId,
          value: medalTrophy
        },
        {
          id: cupTrophyId,
          value: cupTrophy
        },
        {
          id: flameTrophyId,
          value: flameTrophy
        },
        {
          id: starTrophyId,
          value: starTrophy
        },
        {
          id: streakPenaltyId,
          value: streakPenalty
        },
        ]
      }

      const updateGeneralSettingResp = await updateGeneralSetting({ variables: updateGeneralSettingVariables })
      const { result } = updateGeneralSettingResp.data.updateGeneralSetting as IServiceResponse<boolean>
      setIsGeneralSettingUpdated(result)

      setOpenSnackbar(true)
    } catch (e) {
      if (e?.graphQLErrors[0]?.extensions?.code == "authorization") {
        goToLogin()
        return
      }

      console.error(e)
    }
    setIsLoading(false)
  }

  const handleOnCloseSnackbar = () => {
    setOpenSnackbar(false)
  }
  //Max chars for title and body and other data are 4000bytes, but to prevent issue we limit to 1024 for body
  //https://firebase.google.com/docs/cloud-messaging/concept-options
  return <div style={{
    border: '1px solid #dedede',
    padding: '20px',
    borderRadius: '5px',
    background: '#fff',
    width: '400px'
  }}>
    <h3>Trophy Point</h3>
    <Snackbar open={openSnackBar} onClose={handleOnCloseSnackbar} autoHideDuration={6000}>
      <Alert onClose={handleOnCloseSnackbar} severity={isGeneralSettingUpdated ? 'success' : 'error'}>
        {isGeneralSettingUpdated ? 'Trophy Point has successfully updated!' : 'Failed to send Trophy Point!'}
      </Alert>
    </Snackbar>
    {openConfirmationDialog && <ConfirmationDialog open={openConfirmationDialog} handleYes={handleSavePointSetting} handleNo={() => setOpenConfirmationDialog(false)} />}
    <form className={classes.root} noValidate autoComplete="off">
      <FormControl fullWidth>
        <TextField
          autoFocus
          id="MedalTrophy"
          type="number"
          label="Medal Trophy*"
          value={medalTrophy}
          fullWidth
          inputProps={{ maxLength: 50, shrink: true, }}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setMedalTrophy(value)
            setErrorMedalTrophy(value === '')
          }}
        />
        {errorMedalTrophy && <span style={{ color: 'red', fontSize: 12 }}>Medal Trophy Value is required</span>}
      </FormControl>

      <FormControl fullWidth>
        <TextField
          autoFocus
          id="CupTrophy"
          type="number"
          label="Cup Trophy*"
          value={cupTrophy}
          fullWidth
          inputProps={{ maxLength: 50, shrink: true, }}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setCupTrophy(value)
            setErrorCupTrophy(value === '')
          }}
        />
        {errorCupTrophy && <span style={{ color: 'red', fontSize: 12 }}>Cup Trophy Value is required</span>}
      </FormControl>

      <FormControl fullWidth>
        <TextField
          autoFocus
          id="FlameTrophy"
          type="number"
          label="Flame Trophy*"
          value={flameTrophy}
          fullWidth
          inputProps={{ maxLength: 50, shrink: true, }}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setFlameTrophy(value)
            setErrorFlameTrophy(value === '')
          }}
        />
        {errorFlameTrophy && <span style={{ color: 'red', fontSize: 12 }}>Flame Trophy Value is required</span>}
      </FormControl>

      <FormControl fullWidth>
        <TextField
          autoFocus
          id="StarTrophy"
          type="number"
          label="Star Trophy*"
          value={starTrophy}
          fullWidth
          inputProps={{ maxLength: 50, shrink: true, }}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setStarTrophy(value)
            setErrorStarTrophy(value === '')
          }}
        />
        {errorStarTrophy && <span style={{ color: 'red', fontSize: 12 }}>Star Trophy Value is required</span>}
      </FormControl>

      <FormControl fullWidth>
        <TextField
          autoFocus
          id="StreakPenalty"
          type="number"
          label="Streak Penalty*"
          value={streakPenalty}
          fullWidth
          inputProps={{ maxLength: 50, shrink: true, }}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setStreakPenalty(value)
            setErrorStreakPenalty(value === '')
          }}
        />
        {errorStreakPenalty && <span style={{ color: 'red', fontSize: 12 }}>Streak Penalty Value is required</span>}
      </FormControl>

      <FormControl>
        <Button disabled={isLoading} variant="contained" startIcon={<Icon>save</Icon>} onClick={() => {
          const isMedalTrophyEmpty = medalTrophy === ''
          setErrorMedalTrophy(isMedalTrophyEmpty)

          const isCupTrophyEmpty = cupTrophy === ''
          setErrorCupTrophy(isCupTrophyEmpty)

          const isFlameTrophyEmpty = flameTrophy === ''
          setErrorFlameTrophy(isFlameTrophyEmpty)

          const isStarTrophyEmpty = starTrophy === ''
          setErrorStarTrophy(isStarTrophyEmpty)

          const isStreakTrophyEmpty = streakPenalty === ''
          setErrorFlameTrophy(isStreakTrophyEmpty)

          if (isMedalTrophyEmpty || isStarTrophyEmpty || isCupTrophyEmpty || isFlameTrophyEmpty || isStreakTrophyEmpty) {
            return
          }

          const isMedalTrophyInvalid = isNaN(parseFloat(medalTrophy))
          setErrorMedalTrophy(isMedalTrophyInvalid)

          const isCupTrophyInvalid = isNaN(parseFloat(cupTrophy))
          setErrorCupTrophy(isCupTrophyInvalid)

          const isStarTrophyInvalid = isNaN(parseFloat(starTrophy))
          setErrorStarTrophy(isStarTrophyInvalid)

          const isFlameTrophyInvalid = isNaN(parseFloat(flameTrophy))
          setErrorFlameTrophy(isFlameTrophyInvalid)

          const isStreakTrophyInvalid = isNaN(parseFloat(streakPenalty))
          setErrorFlameTrophy(isStreakTrophyInvalid)

          if (isMedalTrophyInvalid || isCupTrophyInvalid || isFlameTrophyInvalid || isStreakTrophyInvalid || isStarTrophyInvalid) {
            return
          }

          setOpenConfirmationDialog(true)
        }} color="primary">
          Update
        </Button>
      </FormControl>
    </form >
  </div >
}

export default TrophyPoint